<template>
  <div
    v-if="show && visibility && displayedHeaders.length > 0"
    class="w-full flex flex-col gap-4 rounded-lg p-2 overflow-x-auto"
    :class="[modernStyle ? 'bg-transparent' : 'bg-dark-bg']"
    :style="{ paddingTop: styled.noPaddingTop ? '1em' : '2rem' }"
  >
    <!-- ───────────────── TITLE & TOOLBAR ───────────────── -->
    <header
      v-if="!modernStyle"
      class="flex flex-wrap items-center gap-4 w-full"
    >
      <!-- Title -->
      <h2 class="text-lg font-semibold text-white whitespace-nowrap">
        {{ table_title }}
      </h2>

      <div
        v-if="showHeaderCheckBox"
        class="flex items-center gap-2 bg-transparent ml-auto"
      >
        <label
          for="show_fechados"
          class="text-sm text-gray-400 whitespace-nowrap"
          >Mostrar Fechados</label
        >
        <input
          id="show_fechados"
          type="checkbox"
          :checked="props.showFechados"
          class="h-4 w-4 rounded border-gray-300 text-blue-safe focus:ring-blue-safe"
          @change="handleShowFechadosChange"
        />
      </div>

      <!-- Search -->
      <div class="relative ml-auto flex items-center">
        <input
          v-model="search_text"
          placeholder="Pesquisar serviço"
          class="w-56 sm:w-72 rounded-md bg-transparent border border-white/30 px-3 py-1 text-sm placeholder-gray-400 focus:ring-0 focus:border-[#1e90ff]"
        />
        <Search class="absolute right-3 h-4 w-4 text-gray-400" />
      </div>
      <!-- Nº Registos selector -->
      <div class="flex items-center gap-1">
        <span class="hidden sm:inline text-sm text-gray-400">Nº Registos</span>
        <select
          v-model="perPage"
          class="rounded bg-[#1a1b29] border border-white/30 text-sm px-2 py-1 focus:outline-none"
        >
          <option v-for="opt in perPageOptions" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
      </div>

      <!-- Extra menu placeholder -->
      <button class="ml-2 text-lg text-gray-300 hover:text-gray-100">
        <i class="fas fa-ellipsis-h" />
      </button>
    </header>

    <!-- ───────────────── TABLE ───────────────── -->
    <div class="w-full overflow-x-auto">
      <div v-if="isMultiSelect" class="relative h-10 mb-16">
        <div
          v-if="isMultiSelect && selectedRows.length > 0"
          class="absolute top-0 left-0 right-0 mx-auto w-full max-w-2xl bg-dark-bg border border-gray-700 rounded-lg shadow-lg p-3 z-50"
        >
          <div class="flex justify-between items-center">
            <div class="text-white">
              {{ selectedRows.length }} item(s) selecionado(s)
            </div>
            <div class="flex gap-2">
              <button
                v-if="multiSelect.buttons.add.show"
                @click="sendSelectedRows(multiSelect.buttons.add.emit)"
                class="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                :disabled="!multiSelect.buttons.add.enabled"
              >
                <i
                  v-if="multiSelect.buttons.add.icon"
                  :class="multiSelect.buttons.add.icon"
                  class="mr-2"
                ></i>
                {{ multiSelect.buttons.add.text || "Adicionar" }}
              </button>
              <button
                v-if="multiSelect.buttons.remove.show"
                @click="sendSelectedRows(multiSelect.buttons.remove.emit)"
                class="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                :disabled="!multiSelect.buttons.remove.enabled"
              >
                <i
                  v-if="multiSelect.buttons.remove.icon"
                  :class="multiSelect.buttons.remove.icon"
                  class="mr-2"
                ></i>
                {{ multiSelect.buttons.remove.text || "Remover" }}
              </button>
              <button
                v-if="multiSelect.buttons.cancel.show"
                @click="sendSelectedRows(multiSelect.buttons.cancel.emit)"
                class="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                :disabled="!multiSelect.buttons.cancel.enabled"
              >
                <i
                  v-if="multiSelect.buttons.cancel.icon"
                  :class="multiSelect.buttons.cancel.icon"
                  class="mr-2"
                ></i>
                {{ multiSelect.buttons.cancel.text || "Cancelar Serviços" }}
              </button>

              <button
                @click="cancelMultiSelect"
                class="px-2 py-1 rounded bg-gray-700 text-white hover:bg-gray-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
        <div
          v-else-if="isMultiSelect && selectedRows.length === 0"
          class="absolute top-0 left-0 right-0 mx-auto w-full max-w-2xl bg-dark-bg border border-gray-700 rounded-lg shadow-lg p-4 z-50"
        >
          <div class="flex items-start space-x-3">
            <div class="flex-shrink-0 text-amber-400">
              <i class="fas fa-info-circle text-xl"></i>
            </div>
            <div class="flex-1">
              <h4 class="font-medium text-white mb-1">
                Selecione os serviços a serem cancelados
              </h4>
              <ul class="text-sm text-gray-300 space-y-1 mt-2">
                <li class="flex items-center">
                  <span
                    class="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"
                  ></span>
                  Apenas serviços com status
                  <span class="font-semibold text-green-400 mx-1">ativo</span>
                  podem ser selecionados
                </li>
                <li class="flex items-center">
                  <span
                    class="inline-block w-2 h-2 rounded-full bg-red-500 mr-2"
                  ></span>
                  Serviços com Código de Barras
                  <span class="font-semibold text-red-400 mx-1">não</span> podem
                  ser selecionados
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <table
        class="w-full border-collapse text-xs text-white"
        :class="{ 'table-fixed': !modernStyle, 'table-auto': modernStyle }"
      >
        <!-- Head -->
        <thead class="bg-dark-bg">
          <tr
            class="border-b"
            :class="modernStyle ? 'border-gray-700' : 'border-white/10'"
          >
            <th
              v-for="header in displayedHeaders"
              :key="header"
              class="px-2 py-2 font-medium uppercase tracking-wide whitespace-nowrap text-gray-300 relative text-left"
            >
              {{ handleTableHeader(header) }}
              <span class="inline-flex flex-col ml-3 h-6 justify-start">
                <!-- Up arrow -->
                <ChevronDown
                  class="w-3 h-3 text-gray-500 transform rotate-180"
                />
                <!-- Down arrow -->
                <ChevronDown class="w-3 h-3 text-gray-500 -mt-1" />
              </span>
            </th>
          </tr>
        </thead>

        <!-- Body -->
        <tbody>
          <tr
            v-for="(row, rowIndex) in mergedTableData"
            :key="row.id || rowIndex"
            @click="
              confirmInclude() && !isMultiSelect
                ? showConfirm($event, row)
                : handleRowClick(row)
            "
            @dblclick="
              confirmInclude() && !isMultiSelect
                ? showConfirm($event, row)
                : handleDoubleClick(row, true)
            "
            :class="[
              isSelectedRow(row) ? 'bg-[#ff9b05]/20' : 'hover:bg-white/5',
              props.applyRowStatusColors ? getRowBackgroundColor(row) : '',
              modernStyle ? 'rounded-md my-1' : 'border-b border-white/10',
            ]"
            :style="{
              height: aplyRowHeight ? '56px' : '32px',
              ...(modernStyle && {
                borderRadius: '8px',
                margin: '14px 0' /* Increased from 6px to 14px */,
                boxShadow:
                  '0 1px 3px rgba(0, 0, 0, 0.1)' /* Subtle shadow for depth */,
              }),
            }"
          >
            <td
              v-for="header in displayedHeaders"
              :key="header"
              :class="[
                'px-2 py-2 border-r border-white/10 last:border-r-0',
                modernStyle ? 'text-left' : 'text-center',
                props.truncateCells ? 'truncate-cell' : '',
              ]"
              :style="{ fontSize: aplyRowHeight ? '14px' : '11px' }"
            >
              <!-- Special rendering for badge columns -->
              <div
                v-if="isBadgeColumn(header)"
                class="inline-block px-2 py-1 rounded-md"
                :class="[
                  getBadgeStyle(header, row[header])?.bgColor,
                  getBadgeStyle(header, row[header])?.textColor,
                  getBadgeStyle(header, row[header])?.bold ? 'font-bold' : '',
                  getBadgeStyle(header, row[header])?.textTransform,
                ]"
              >
                {{ handleValue(row[header]) }}
              </div>
              <div v-else-if="isDateColumn(header)" class="date-format">
                <div class="date-day-month font-bold text-lg">
                  {{ formatDateValue(row[header]).dayMonth }}
                </div>
                <div class="date-year text-gray-400">
                  {{ formatDateValue(row[header]).year }}
                </div>
              </div>
              <div
                v-else-if="isCurrencyColumn(header)"
                class="text-lg font-bold"
              >
                <span>{{ currencySymbol }}</span
                >{{ formatCurrencyValue(row[header]) }}
              </div>
              <div v-else>
                {{ handleValue(row[header]) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ───────────────── Pagination ───────────────── -->
    <footer
      class="flex items-center justify-center gap-3 text-sm p-3 rounded-md"
      :class="modernStyle ? 'bg-dark-bg' : ''"
    >
      <button
        @click="prevPage"
        :disabled="currentPage === 1"
        class="text-[#1db8ff] disabled:text-gray-600"
      >
        ◀
      </button>
      <span>Página {{ currentPage }} de {{ totalPages }}</span>
      <button
        @click="nextPage"
        :disabled="currentPage === totalPages"
        class="text-[#1db8ff] disabled:text-gray-600"
      >
        ▶
      </button>
    </footer>

    <!-- Popups -->
    <ConfirmPopUp
      ref="myConfirm"
      @accept="onAccept"
      @accept-one="AcceptOne"
      @accept-both="onAcceptBoth"
      :message="confirmationProps.message"
    />
    <PopupPainel
      v-if="status == 0"
      :loading="true"
      message="A carregar dados..."
      :bg="true"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { ChevronDown, Search } from "lucide-vue-next";
import AppButton from "@/components/button.vue";
import AppSearch from "@/components/search.vue";
import AppTableFilter from "@/components/filter/tableFilter.vue";
import ConfirmPopUp from "@/components/modals/MyConfirmPopUp.vue";
import PopupPainel from "@/components/_popupPainel.vue";
import { toast } from "vue3-toastify";

// Props
const props = defineProps({
  modernStyle: {
    type: Boolean,
    default: false,
  },
  showHeaderCheckBox: {
    type: Boolean,
    default: false,
  },
   showFechados: {
    type: Boolean,
    default: true,
  },

  extractEquipamentoType: {
    type: Boolean,
    default: false,
  },
  truncateCells: {
    type: Boolean,
    default: false,
  },
  hiddenHeaders: {
    type: Array,
    default: () => ["id"],
  },
  styled: {
    type: Object,
    default: () => ({}),
  },
  hiden: {
    type: Object,
    default: () => ({
      status: false,
    }),
  },
  noClose: {
    type: Boolean,
    default: () => false,
  },
  specialKey: {
    type: String,
    default: () => null,
  },
  page_display: {
    type: Number,
    default: () => 10,
  },
  simple: {
    type: Boolean,
    default: () => false,
  },
  table_data: {
    type: Array,
    default: () => [],
  },
  btn_criar: {
    type: Object,
    default: () => ({
      show: false,
      form: "0",
    }),
  },
  btn_adicionar: {
    type: Boolean,
    default: () => false,
  },
  status: {
    type: String,
    default: () => "200",
  },
  confirmationProps: {
    type: Object,
    default: () => ({
      show: false,
      message: "Deseja adicionar este item?",
    }),
  },
  table_title: {
    type: String,
    default: () => "Table Title",
  },
  show: {
    type: Boolean,
    default: () => false,
  },
  visibility: {
    type: Boolean,
    default: () => false,
  },
  notificacao: {
    type: Object,
    default: () => ({}),
  },
  with_confirmation: {
    type: Boolean,
    default: () => false,
  },
  pickKey: {
    type: Array,
    default: () => [],
  },
  applyRowStatusColors: {
    type: Boolean,
    default: false,
  },
  rowStatusData: {
    type: Array,
    default: () => [],
  },
  selectedRowId: {
    type: [Number, String],
    default: null,
  },
  check_codigo_barra: {
    type: Boolean,
    default: false,
  },
  aplyRowHeight: {
    type: Boolean,
    default: false,
  },
  multiSelect: {
    type: Object,
    default: () => ({
      show: false,
      isActive: false,
      buttons: {
        add: {
          show: false,
          enabled: false,
          text: "Adicionar",
          color: "green",
          icon: "fa-solid fa-plus",
          emit: "send-selected-rows",
        },
        remove: {
          show: false,
          enabled: false,
          text: "Remover",
          color: "red",
          icon: "fa-solid fa-minus",
          emit: "remove-selected-rows",
        },
      },
    }),
  },
});

// Emits
const emit = defineEmits([
  "row-clicked",
  "row-doClicked",
  "codigo-barra-error",
  "delete_data",
  "close",
  "criar-servico",
  "send-selected-rows",
  "remove-selected-rows",
  "show-fechados",
]);

// Refs
const myConfirm = ref(null);
const filter_data = ref({});
const search_text = ref("");
const perPage = ref(props.page_display);
const currentPage = ref(1);
const selectedRow = ref({});
const isMultiSelect = ref(props.multiSelect.isActive);
const selectedRows = ref([]);
const currencySymbol = ref("€");

const tipoStyleMap = ref({
  M: {
    bgColor: "bg-blue-600/20",
    textColor: "text-blue-300",
    bold: true,
    textTransform: "uppercase",
  },
  I: {
    bgColor: "bg-green-600/20",
    textColor: "text-green-300",
    bold: true,
    textTransform: "uppercase",
  },
  R: {
    bgColor: "bg-purple-600/20",
    textColor: "text-purple-300",
    bold: true,
    textTransform: "uppercase",
  },
  E: {
    bgColor: "bg-yellow-600/20",
    textColor: "text-yellow-300",
    bold: true,
    textTransform: "uppercase",
  },
  // Default style for any other letter
  default: {
    bgColor: "bg-gray-600/20",
    textColor: "text-gray-200",
    bold: true,
    textTransform: "uppercase",
  },
});

const familiaStyleMap = ref({
  Extintores: {
    bgColor: "bg-blue-600/20",
    textColor: "text-white",
    bold: true,
  },
  CARRETEIS: {
    bgColor: "bg-cyan-600/20",
    textColor: "text-white",
    bold: true,
  },
  default: {
    bgColor: "bg-gray-600/20",
    textColor: "text-gray-200",
    bold: false,
  },
});

const statusStyleMap = ref({
  execução: {
    bgColor: "bg-green-600/20",
    textColor: "text-green-300",
    bold: true,
    textTransform: "uppercase",
  },
  transferido: {
    bgColor: "bg-yellow-600/20",
    textColor: "text-yellow-200",
    bold: true,
    textTransform: "uppercase",
  },
  interrompido: {
    bgColor: "bg-red-600/20",
    textColor: "text-red-300",
    bold: true,
    textTransform: "uppercase",
  },
  // Add these new status styles
  rejeitado: {
    bgColor: "bg-gray-200",
    textColor: "text-red-600",
    bold: true,
    textTransform: "uppercase",
  },
  local: {
    bgColor: "bg-blue-500",
    textColor: "text-blue-200",
    bold: true,
    textTransform: "uppercase",
  },
  fechado: {
    bgColor: "bg-gray-200",
    textColor: "text-green-500",
    bold: true,
    textTransform: "uppercase",
  },
  cancelado: {
    bgColor: "bg-gray-200",
    textColor: "text-red-500",
    bold: true,
    textTransform: "uppercase",
  },
  substituido: {
    bgColor: "bg-purple-600/20",
    textColor: "text-purple-200",
    bold: true,
    textTransform: "uppercase",
  },
  recolhido: {
    bgColor: "bg-orange-600/20",
    textColor: "text-orange-300",
    bold: true,
    textTransform: "uppercase",
  },
  default: {
    bgColor: "bg-gray-600/20",
    textColor: "text-gray-200",
    bold: false,
  },
  ativo: {
    bgColor: "bg-green-600/20",
    textColor: "text-green-200",
    bold: true,
    textTransform: "uppercase",
  },
 
});

function cancelMultiSelect() {
  selectedRows.value = [];
}

// Lifecycle hooks
onMounted(() => {
  if (props.selectedRowId !== null) {
    const foundRow = props.table_data.find(
      (row) => row.id == props.selectedRowId
    );
    if (foundRow) {
      selectedRow.value = foundRow;
    }
  }
  console.log("TecnicoTable mounted with props:", props);
});

// Watchers
watch(
  () => props.table_data,
  (newVal) => {
    if (newVal.length > 0) {
      filter_data.value = addTrueValues(newVal[0]);
      currentPage.value = 1;
      search_text.value = "";

      // Update the selected row with the latest data, if it's present
      if (selectedRow.value && selectedRow.value.id) {
        const updatedRow = newVal.find(
          (row) => row.id === selectedRow.value.id
        );
        if (updatedRow) {
          selectedRow.value = updatedRow;
        }
      }
    }
  },
  { immediate: true, deep: true }
);

watch(search_text, () => {
  currentPage.value = 1;
});

watch(
  () => props.selectedRowId,
  (newId) => {
    if (newId !== null) {
      const foundRow = props.table_data.find((row) => row.id == newId);
      if (foundRow) {
        selectedRow.value = foundRow;
      }
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.showFechados,
  () => {
    currentPage.value = 1;
  }
);

watch(
  () => props.multiSelect.isActive,
  (newValue) => {
    console.log("Multi-select mode isActive changed:", newValue);

    // Update local ref
    isMultiSelect.value = newValue;

    // Clear selections when deactivated
    if (!newValue) {
      console.log("Multi-select mode deactivated, clearing selections");
      selectedRows.value = [];
    }
  },
  { immediate: true } // Run immediately on component creation
);

// Add this new computed property before mergedTableData
const filteredByStatusData = computed(() => {
  // If showFechados is true, show all rows
  if (props.showFechados) {
    return searchedTableData.value;
  }

  // Otherwise filter out any rows with status "fechado"
  return searchedTableData.value.filter((row) => row.status !== "fechado");
});

// Then modify the displayedTableData to use filteredByStatusData instead
const displayedTableData = computed(() => {
  const startIndex = (currentPage.value - 1) * perPage.value;
  const endIndex = startIndex + perPage.value;
  return filteredByStatusData.value.slice(startIndex, endIndex);
});

// And update totalPages to use the filtered data
const totalPages = computed(() => {
  return Math.ceil(filteredByStatusData.value.length / perPage.value);
});

// Methods

function extractServiceInfo(row) {
  let serviceType = "N/A";
  let equipmentInfo = "N/A";

  if (
    row.servico &&
    typeof row.servico === "object" &&
    row.servico.nome &&
    typeof row.servico.nome === "string"
  ) {
    const parts = row.servico.nome.split(" ");
    serviceType = parts[0]; // First word
    equipmentInfo = parts.slice(1).join(" "); // Rest of the string
  } else if (
    row.EquipamentoServico &&
    typeof row.EquipamentoServico === "object" &&
    row.EquipamentoServico.nome &&
    typeof row.EquipamentoServico.nome === "string"
  ) {
    const parts = row.EquipamentoServico.nome.split(" ");
    serviceType = parts[0]; // First word
    equipmentInfo = parts.slice(1).join(" "); // Rest of the string
  }

  return { tipo: serviceType, equipamento: equipmentInfo };
}

// Function to check if a column is a currency column
function getRowBackgroundColor(row) {
  // Selected row handling stays the same
  if (isSelectedRow(row)) {
    return "bg-[#ff9b05]/20";
  }

  // For status colors, return additional class name for modern styling
  if (props.applyRowStatusColors) {
    const statusEntry = props.rowStatusData.find(
      (entry) => entry.id === row.id
    );
    if (statusEntry) {
      const status = statusEntry.status;
      // Return both a regular bg class and a modern-style class
      if (props.modernStyle) {
        return `status-${status}`; // Custom class that will override modern style
      }
      // Regular styling for non-modern mode
      switch (status) {
        case "rejeitado":
          return "bg-red-600/50";
        case "local":
          return "bg-blue-500/20";
        case "fechado":
          return "bg-lime-600/40";
        case "cancelado":
          return "bg-red-500/40";
        case "substituido":
          return "bg-purple-500/30";
        case "recolhido":
          return "bg-orange-500/30";
        default:
          break;
      }
    }
  }

  // For regular row statuses
  if (row.status === "ativo") {
    // Return a custom class for modern style
    return props.modernStyle ? "status-active" : "bg-gray-600/20";
  } else if (row.status === "desativado") {
    return props.modernStyle ? "status-disabled" : "bg-red-500/20";
  } else if (row.status === "fechado") {
    return props.modernStyle ? "status-closed" : "bg-green-500/20";
  } else if (row.status === "interrompido") {
    return props.modernStyle ? "status-interrupted" : "bg-amber-500/20";
  }

  return "";
}

function isCurrencyColumn(header) {
  return (
    header.toLowerCase() === "valor" ||
    header.toLowerCase() === "price" ||
    header.toLowerCase() === "preco" ||
    header.toLowerCase().includes("value")
  );
}

// Function to format currency values
function formatCurrencyValue(value) {
  if (value === null || value === undefined || value === "") return "N/A";

  // If it's already a number or a string representing a number
  let numValue =
    typeof value === "number"
      ? value
      : parseFloat(String(value).replace(",", "."));

  if (isNaN(numValue)) return value;

  // Format with 2 decimal places
  return numValue.toFixed(2);
}
function isDateColumn(header) {
  // Match common date column names
  return (
    header.toLowerCase().includes("data") ||
    header.toLowerCase().includes("date") ||
    header.endsWith("At") ||
    header === "inicio" ||
    header === "fim"
  );
}

// Format date values with month/day (bold) and year underneath
function formatDateValue(dateString) {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return {
      dayMonth: `${day}/${month}`,
      year: year.toString(),
    };
  } catch (e) {
    return dateString;
  }
}

function isBadgeColumn(header) {
  return header === "familia" || header === "status" || header === "tipo";
}

function getBadgeStyle(header, value) {
  if (header === "familia") {
    // Find style for this familia value or use default
    return familiaStyleMap.value[value] || familiaStyleMap.value.default;
  }

  if (header === "status") {
    // Find style for this status value or use default
    return statusStyleMap.value[value] || statusStyleMap.value.default;
  }

  if (header === "tipo") {
    return tipoStyleMap.value[value] || tipoStyleMap.value.default;
  }

  return null;
}
function handleTableHeader(key) {
  // Special cases
  if (key === "localInstalacao") {
    return "Local";
  }

  if (key === "CodigoBarra") {
    return "Cód. Barras";
  }
  if (key === "observacao") {
    return "Observações";
  }
  if (key === "NumeroSerie") {
    return "Nº Série";
  }
  if (key === "NumeroIdentificacao") {
    return "Nº Identificação";
  }
  if (key === "Localizacao") {
    return "Localização";
  }

  // Regular camelCase formatting
  const isCamelCase = /[a-z][A-Z]/.test(key);
  if (isCamelCase) {
    key = key.replace(/([A-Z])/g, " $1").toLowerCase();
  }

  return key;
}

function openFormCriar() {
  switch (props.btn_criar.form) {
    case "0":
      console.log("Sem form");
      break;
    case "pedido":
      // You'll need to adapt this to use a different state management approach
      // this.$store.state.clientes.formPedidoStatus = true;
      break;
    default:
      break;
  }
}

function handleValue(value) {
  if (typeof value === "object") {
    if (
      value === null ||
      value === undefined ||
      Object.keys(value).length === 0
    ) {
      return "N/A";
    }
  }
  if (typeof value === "object" && value !== null && value !== undefined) {
    if ("nome" in value) {
      return value.nome;
    } else if ("user" in value) {
      return value.user;
    } else if (props.specialKey != null) {
      return value[props.specialKey];
    } else {
      return value;
    }
  } else {
    return value;
  }
}

function handleDoubleClick(row, doubleClick = false) {
  emit("row-doClicked", row, doubleClick);
}

function handleShowFechadosChange(event) {
  const newValue = event.target.checked;
  emit("show-fechados", newValue);
  console.log("Show Fechados changed to:", newValue);
}

function handleRowClick(row) {
  // Multi-select branch
  if (isMultiSelect.value) {
    if (row.status !== "ativo") {
      // Show toast or alert that only active items can be canceled
      toast.error("Apenas serviços ativos podem ser selecionados.");
      return;
    }

    if (row.CodigoBarra !== "N/A") {
      // Show toast or alert that items with Código de Barras cannot be selected
      toast.error("Serviços com Código de Barras não podem ser selecionados.");
      return;
    }

    // Now handle the selection/deselection
    const index = selectedRows.value.findIndex(
      (selected) => selected.id === row.id
    );
    if (index === -1) {
      selectedRows.value.push(row);
    } else {
      selectedRows.value.splice(index, 1);
    }
    return;
  }

  if (selectedRow.value && selectedRow.value.id === row.id) {
    selectedRow.value = row;
    emit("row-clicked", row);
    return;
  }
  // When check_codigo_barra is enabled, validate only the currently selected row
  if (props.check_codigo_barra && Object.keys(selectedRow.value).length > 0) {
    if (checkCodigoBarra(selectedRow.value)) {
      emit("codigo-barra-error");
      return;
    }
  }

  // If there is no old selection or the old selection is valid, update to the new row
  selectedRow.value = row;
  emit("row-clicked", row);
}

function checkCodigoBarra(item) {
  const validStatuses = ["ativo", "fechado"];
  // If the item is empty or its status isn't one of the valid ones, skip the check
  if (Object.keys(item).length === 0 || !validStatuses.includes(item.status)) {
    return false;
  }
  // Check if Código de Barras is null, empty, or "N/A"
  if (
    !item.CodigoBarra ||
    item.CodigoBarra.trim() === "" ||
    item.CodigoBarra === "N/A"
  ) {
    return true;
  }
  return false;
}

function criarServico() {
  emit("criar-servico");
}



function delete_onClick(row) {
  emit("delete_data", row);
}

function close() {
  // Adapt your store management here
  // this.$store.state.clientes.showRightForm = true;
  emit("close");
}

function addTrueValues(obj) {
  let newObj = {};
  for (let key in obj) {
    newObj[key] = true;
  }
  return newObj;
}

function updateFilter({ property, value }) {
  filter_data.value[property] = value;
}

function getSearch(text) {
  search_text.value = text;
}

function applySearchFilter(data) {
  if (!search_text.value) {
    return data;
  }
  return data.filter((row) => {
    for (let key in row) {
      if (
        Object.prototype.hasOwnProperty.call(row, key) &&
        typeof row[key] === "string" &&
        row[key].toLowerCase().includes(search_text.value.toLowerCase())
      ) {
        return true;
      } else if (
        Object.prototype.hasOwnProperty.call(row, key) &&
        typeof row[key] === "number" &&
        row[key] == parseInt(search_text.value)
      ) {
        return true;
      }
    }
    return false;
  });
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
}

function showConfirm(event, row) {
  if (isMultiSelect.value) {
    console.log("Confirmed multi!");
    myConfirm.value.show(event.target);
    return;
  }
  selectedRow.value = row;
  myConfirm.value.show(event.target);
}

function onAccept() {
  handleRowClick(selectedRow.value);
}

function AcceptOne() {
  console.log("Confirmed one!");
  const permissoes = 1;
  handleRowClick(selectedRow.value, permissoes);
}

function onAcceptBoth() {
  console.log("Confirmed both!");
  const permissoes = 2;
  handleRowClick(selectedRow.value, permissoes);
}

function confirmInclude() {
  return props.confirmationProps.show;
}

function isSelectedRow(row) {
  if (isMultiSelect.value) {
    return selectedRows.value.some((selectedRow) => selectedRow.id === row.id);
  }
  return selectedRow.value.id === row.id;
}

function sendSelectedRows(emit_type) {
  console.log("selected rows:", selectedRows.value);
  emit(
    emit_type,
    selectedRows.value.sort((a, b) => a.id - b.id)
  );
}

// Computed properties
const perPageOptions = computed(() => [5, 10, 15, 20]);

const filteredTableData = computed(() => {
  return props.table_data.map((row) => {
    let filteredRow = {};

    // Copy existing data that passes filter
    for (let key in row) {
      // Skip original service fields when extracting type - we'll handle them differently
      if (
        props.extractEquipamentoType &&
        (key === "servico" || key === "EquipamentoServico")
      ) {
        continue;
      }

      if (filter_data.value[key]) {
        filteredRow[key] = row[key];
      }
    }

    // When extraction is enabled, add both tipo and equipamento columns
    if (props.extractEquipamentoType) {
      const serviceInfo = extractServiceInfo(row);
      filteredRow.tipo = serviceInfo.tipo;
      filteredRow.equipamento = serviceInfo.equipamento;
    }

    return filteredRow;
  });
});

const displayedHeaders = computed(() => {
  if (filteredTableData.value.length > 0) {
    return Object.keys(filteredTableData.value[0]).filter((key) => {
      const lowerKey = key.toLowerCase();

      return !props.hiddenHeaders.some((hidden) => {
        const lowerHidden = hidden.toLowerCase();

        if (lowerKey === lowerHidden) return true;

        if (lowerKey === lowerHidden.replace(/_/g, "")) return true;

        const camelKey = lowerKey
          .replace(/([a-z])([A-Z])/g, "$1_$2")
          .toLowerCase();
        return camelKey === lowerHidden;
      });
    });
  }
  return [];
});

const searchedTableData = computed(() => {
  return applySearchFilter(filteredTableData.value);
});

// const totalPages = computed(() => {
//   return Math.ceil(searchedTableData.value.length / perPage.value);
// });

// const displayedTableData = computed(() => {
//   const startIndex = (currentPage.value - 1) * perPage.value;
//   const endIndex = startIndex + perPage.value;
//   return searchedTableData.value.slice(startIndex, endIndex);
// });

const mergedTableData = computed(() => {
  const mergedData = [];
  const filteredData = filteredTableData.value;
  const displayedData = displayedTableData.value;
  if (filteredData && filteredData.length > 0) {
    const filteredKeys = Object.keys(filteredData[0]);
    for (let i = 0; i < displayedData.length; i++) {
      const row = displayedData[i];
      const mergedRow = {};
      for (const key of filteredKeys) {
        mergedRow[key] = row[key];
      }
      mergedData.push(mergedRow);
    }
  }
  return mergedData;
});
</script>

<style scoped>
/* Badge styling */
.inline-block.px-2.py-1.rounded-md {
  min-width: 80px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-overflow: ellipsis;
  overflow: hidden;
}

/* Base table structure */
table {
  table-layout: fixed;
  width: 100%;
  min-width: 900px;
  border-spacing: 0;
}

/* Header styling */
thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

th {
  font-weight: 600;
  padding: 12px 8px;
  text-align: left;
  position: relative;
}

/* Cell styling - MODIFIED to disable truncation */
th,
td {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  min-width: 100px;
  transition: background-color 0.15s ease;
}

td {
  padding: 8px;
  vertical-align: middle;
}

/* Modern style - Table */
:deep(.table-auto) {
  border-collapse: separate;
  border-spacing: 0 10px;
}

/* Modern style rows */
tbody tr.rounded-md {
  isolation: isolate;
  position: relative;
  background-color: rgba(18, 18, 32, 0.85);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  margin: 16px 0 !important; /* Increased spacing between rows */
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Modern style rows hover */
tbody tr.rounded-md:hover:not(.bg-\[\#ff9b05\]\/20) {
  background-color: rgba(25, 25, 35, 0.7) !important;
}

/* Selected row styling */
tr.rounded-md.bg-\[\#ff9b05\]\/20 {
  box-shadow: none !important;
  border: 2px solid rgba(255, 155, 5, 0.5) !important;
  background-color: rgba(
    45,
    45,
    55,
    0.8
  ) !important; /* Slightly different for selected */
}

/* Border radius for cells in modern style */
.rounded-md td:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  padding-left: 12px;
}

.rounded-md td:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  padding-right: 12px;
}

/* Remove borders between cells in modern style */
.rounded-md td {
  border-right: none !important;
  padding-top: 12px;
  padding-bottom: 12px;
}

/* Column-specific width settings */
th[data-header="familia"],
td[data-header="familia"] {
  min-width: 120px;
}

th[data-header="status"],
td[data-header="status"] {
  min-width: 130px;
}

th[data-header="valor"],
td[data-header="valor"] {
  min-width: 100px;
}

th[data-header="tipo"],
td[data-header="tipo"] {
  min-width: 100px;
  max-width: 120px;
}

th[data-header="equipamento"],
td[data-header="equipamento"] {
  min-width: 150px;
  max-width: 250px;
}

th[data-header="observacao"],
td[data-header="observacao"] {
  min-width: 150px;
  max-width: 250px;
}

th[data-header="codigoBarra"],
td[data-header="codigoBarra"],
th[data-header="CodigoBarra"],
td[data-header="CodigoBarra"] {
  min-width: 120px;
}

th[data-header="numeroSerie"],
td[data-header="numeroSerie"] {
  min-width: 120px;
}

th[data-header="localizacao"],
td[data-header="localizacao"],
th[data-header="localInstalacao"],
td[data-header="localInstalacao"] {
  min-width: 130px;
}

/* Date column formatting */
.date-format {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.date-day-month {
  font-size: 0.95rem;
  line-height: 1.2;
  font-weight: bold;
}

.date-year {
  font-size: 0.75rem;
  line-height: 1;
  color: rgba(156, 163, 175);
}

/* Currency formatting */
.text-lg.font-bold {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
}

/* Footer/pagination styling */
footer {
  transition: background-color 0.2s ease;
}

footer button {
  transition: color 0.2s ease;
  padding: 0 8px;
}

footer button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Space between table and pagination */

/* Empty state and error messages */
.w-full.py-6.text-center {
  padding: 2rem 0;
  font-weight: 500;
  border-radius: 8px;
}

tr.rounded-md.status-rejeitado {
  background-color: rgba(220, 38, 38, 0.5) !important;
}

tr.rounded-md.status-local {
  background-color: rgba(59, 130, 246, 0.2) !important;
}

tr.rounded-md.status-fechado {
  background-color: rgba(132, 204, 22, 0.4) !important;
}

tr.rounded-md.status-cancelado {
  background-color: rgba(239, 68, 68, 0.4) !important;
}

tr.rounded-md.status-substituido {
  background-color: rgba(168, 85, 247, 0.3) !important;
}

tr.rounded-md.status-recolhido {
  background-color: rgba(249, 115, 22, 0.3) !important;
}

tr.rounded-md.status-active {
  background-color: rgba(18, 18, 32, 0.85) !important; /* Same as default */
}

tr.rounded-md.status-disabled {
  background-color: rgba(239, 68, 68, 0.2) !important;
}

tr.rounded-md.status-closed {
  background-color: rgba(34, 197, 94, 0.2) !important;
}

tr.rounded-md.status-interrupted {
  background-color: rgba(245, 158, 11, 0.2) !important;
}

/* Improve responsiveness */
@media (max-width: 768px) {
  th[data-header],
  td[data-header] {
    min-width: 80px; /* Smaller minimum widths on mobile */
  }

  .inline-block.px-2.py-1.rounded-md {
    min-width: 60px;
  }
}

/* Modern style text alignment fixes */
.table-auto tbody tr td.text-left {
  text-align: left !important;
}

/* Keep badges from wrapping */
.inline-block.px-2.py-1.rounded-md {
  white-space: nowrap !important;
}

/* Alignment fixes for different cell types */
.text-left > div:not(.date-format):not(.inline-block) {
  text-align: left !important;
}

/* Keep date formatting centered */
.date-format {
  display: flex !important;
  flex-direction: column;
  align-items: center;
}

/* Adjust currency display in modern style */
.text-left > .text-lg.font-bold {
  justify-content: flex-start !important;
}

/* Keep centered alignment in non-modern style */
.text-center > div:not(.date-format):not(.inline-block) {
  text-align: center;
}

/* Truncate cell class - disable truncation */
.truncate-cell {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
}
</style>
